
import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useUpdateCampaign } from "@/hooks/useUpdateCampaign";
import { useAuth } from "@/contexts/AuthContext";
import { convertDbToDisplay, convertDisplayToDb, getCurrencySymbol } from "@/utils/currency";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: any;
}

const EditCampaignModal: React.FC<Props> = ({ open, onOpenChange, campaign }) => {
  const { userCountry } = useAuth();
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    status: "draft",
    start_date: "",
    end_date: "",
    total_budget: "",
  });

  const updateCampaignMutation = useUpdateCampaign();

  // Update form data when campaign prop changes
  useEffect(() => {
    if (campaign) {
      const rawBudget = typeof campaign.total_budget === 'number' ? campaign.total_budget : 0;
      const displayBudget = convertDbToDisplay(rawBudget, userCountry);

      setFormData({
        name: campaign.name || "",
        description: campaign.description || "",
        status: campaign.status || "draft",
        start_date: campaign.start_date || campaign.startDate || "",
        end_date: campaign.end_date || campaign.endDate || "",
        total_budget: isNaN(displayBudget) || displayBudget === 0 ? "" : displayBudget.toString(),
      });
    }
  }, [campaign, userCountry]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const inputBudget = formData.total_budget ? parseFloat(formData.total_budget) : null;
    const dbBudget = inputBudget !== null ? convertDisplayToDb(inputBudget, userCountry) : null;

    const currentStage = campaign.kanban_stage || null;
    let newStage = currentStage;

    if (formData.status === 'active') {
      if (currentStage !== 'in_progress') {
        newStage = 'in_progress';
      }
    } else if (formData.status === 'draft') {
      newStage = 'discovery';
    } else if (formData.status === 'completed') {
      newStage = 'completed';
    }

    const updates = {
      name: formData.name,
      description: formData.description,
      status: formData.status,
      start_date: formData.start_date || null,
      end_date: formData.end_date || null,
      total_budget: dbBudget,
      kanban_stage: newStage,
    };

    updateCampaignMutation.mutate(
      { campaignId: campaign.id, updates },
      {
        onSuccess: () => {
          onOpenChange(false);
        },
      }
    );
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Campaign</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Campaign Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={(value) => handleInputChange("status", value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start_date">Start Date</Label>
              <Input
                id="start_date"
                type="date"
                value={formData.start_date}
                onChange={(e) => handleInputChange("start_date", e.target.value)}
              />
            </div>
            
            <div>
              <Label htmlFor="end_date">End Date</Label>
              <Input
                id="end_date"
                type="date"
                value={formData.end_date}
                onChange={(e) => handleInputChange("end_date", e.target.value)}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="total_budget">Total Budget ({getCurrencySymbol(userCountry)})</Label>
            <Input
              id="total_budget"
              type="number"
              value={formData.total_budget}
              onChange={(e) => handleInputChange("total_budget", e.target.value)}
              placeholder={`Enter amount in ${userCountry && (userCountry.toLowerCase() === 'india' || userCountry.toLowerCase() === 'in') ? 'INR' : 'USD'}`}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={updateCampaignMutation.isPending}
              className='bg-purple-600 hover:bg-purple-700 text-white'
            >
              {updateCampaignMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditCampaignModal;
